import React from "react";
import { cn } from "@/lib/utils";

// ============================================================
// FYUNOR — Typography Scale
// "Computational Minimalism" type system.
// Every text element in the platform resolves to one of these.
// ============================================================

const displayClass =
  "font-display font-bold tracking-[var(--tracking-display)] leading-[1.05] text-balance";
const headingClass =
  "font-heading font-semibold tracking-[var(--tracking-heading)] leading-[1.15] text-balance";

export function Display({ as: Comp = "h1", className, children, ...props }) {
  return (
    <Comp className={cn(displayClass, "text-4xl sm:text-5xl lg:text-6xl xl:text-[64px]", className)} {...props}>
      {children}
    </Comp>
  );
}

export function PageHeading({ as: Comp = "h2", className, children, ...props }) {
  return (
    <Comp className={cn(headingClass, "text-2xl lg:text-[32px]", className)} {...props}>
      {children}
    </Comp>
  );
}

export function SectionHeading({ as: Comp = "h3", className, children, ...props }) {
  return (
    <Comp className={cn(headingClass, "text-xl lg:text-2xl", className)} {...props}>
      {children}
    </Comp>
  );
}

export function SubsectionHeading({ as: Comp = "h4", className, children, ...props }) {
  return (
    <Comp className={cn(headingClass, "text-base lg:text-lg", className)} {...props}>
      {children}
    </Comp>
  );
}

export function Body({ as: Comp = "p", className, children, ...props }) {
  return (
    <Comp className={cn("font-body text-base leading-[1.6] text-foreground/90", className)} {...props}>
      {children}
    </Comp>
  );
}

export function BodySmall({ as: Comp = "p", className, children, ...props }) {
  return (
    <Comp className={cn("font-body text-sm leading-[1.6] text-muted-foreground", className)} {...props}>
      {children}
    </Comp>
  );
}

export function Label({ as: Comp = "label", className, children, ...props }) {
  return (
    <Comp className={cn("font-body text-sm font-medium leading-none text-foreground", className)} {...props}>
      {children}
    </Comp>
  );
}

export function Caption({ as: Comp = "span", className, children, ...props }) {
  return (
    <Comp className={cn("font-body text-xs leading-[1.4] tracking-wide text-subtle-foreground uppercase", className)} {...props}>
      {children}
    </Comp>
  );
}

export function HelperText({ as: Comp = "p", className, children, ...props }) {
  return (
    <Comp className={cn("font-body text-xs leading-[1.5] text-muted-foreground", className)} {...props}>
      {children}
    </Comp>
  );
}

/** Numeric display — monospace, tabular figures for aligned data. */
export function Mono({ as: Comp = "span", className, children, ...props }) {
  return (
    <Comp className={cn("font-mono tabular", className)} {...props}>
      {children}
    </Comp>
  );
}